import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiCircleHalfBold,
    PiFloppyDiskBold,
    PiArrowCounterClockwiseBold,
    PiSpinnerBold,
} from 'react-icons/pi';
import { Button, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';

type StageSetting = {
    stage: string;
    label: string;
    row_color: string | null;
};

const PRESET_COLORS = [
    '#ef4444', // vermelho
    '#f97316', // laranja
    '#eab308', // amarelo
    '#22c55e', // verde
    '#06b6d4', // ciano
    '#3b82f6', // azul
    '#8b5cf6', // roxo
    '#ec4899', // rosa
    '#f59e0b', // âmbar
    '#10b981', // esmeralda
    '#6366f1', // índigo
    '#14b8a6', // teal
];

function hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

type ColorPickerProps = {
    value: string | null;
    onChange: (color: string | null) => void;
};

function ColorPicker({ value, onChange }: ColorPickerProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    return (
        <div className="flex flex-wrap items-center gap-2">
            {/* Swatches de preset */}
            {PRESET_COLORS.map((color) => (
                <button
                    key={color}
                    type="button"
                    title={color}
                    onClick={() => onChange(color)}
                    className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-110"
                    style={{
                        backgroundColor: color,
                        borderColor: value === color ? '#1a1a1a' : 'transparent',
                        outline: value === color ? `2px solid ${color}` : 'none',
                        outlineOffset: '2px',
                    }}
                />
            ))}

            {/* Cor personalizada */}
            <button
                type="button"
                title="Escolher cor personalizada"
                onClick={() => inputRef.current?.click()}
                className="relative flex h-7 w-7 items-center justify-center rounded-full border-2 border-dashed border-gray-300 text-gray-400 hover:border-gray-500"
                style={value && !PRESET_COLORS.includes(value) ? { backgroundColor: value, borderColor: 'transparent' } : {}}
            >
                <PiCircleHalfBold className="h-3.5 w-3.5" />
                <input
                    ref={inputRef}
                    type="color"
                    value={value ?? '#ffffff'}
                    onChange={(e) => onChange(e.target.value)}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                />
            </button>

            {/* Limpar cor */}
            {value && (
                <button
                    type="button"
                    title="Remover cor"
                    onClick={() => onChange(null)}
                    className="rounded px-2 py-0.5 text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
                >
                    Remover
                </button>
            )}
        </div>
    );
}

export default function StageColorsPage() {
    const qc = useQueryClient();
    const [settings, setSettings] = useState<StageSetting[]>([]);
    const [dirty, setDirty] = useState(false);

    const { data, isLoading } = useQuery<StageSetting[]>({
        queryKey: ['stage-settings'],
        queryFn: async () => {
            const { data } = await api.get<{ data: StageSetting[] }>('/stage-settings');
            return data.data;
        },
    });

    useEffect(() => {
        if (data) {
            setSettings(data.map((s) => ({ ...s })));
            setDirty(false);
        }
    }, [data]);

    const saveMutation = useMutation({
        mutationFn: async () => {
            await api.put('/stage-settings', { settings });
        },
        onSuccess: () => {
            toast.success('Cores dos estágios salvas com sucesso!');
            qc.invalidateQueries({ queryKey: ['stage-settings'] });
            qc.invalidateQueries({ queryKey: ['shipments'] });
            setDirty(false);
        },
        onError: () => toast.error('Erro ao salvar configurações.'),
    });

    const handleColorChange = (stage: string, color: string | null) => {
        setSettings((prev) =>
            prev.map((s) => (s.stage === stage ? { ...s, row_color: color } : s))
        );
        setDirty(true);
    };

    const resetAll = () => {
        if (data) {
            setSettings(data.map((s) => ({ ...s })));
            setDirty(false);
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Cores dos Estágios"
                breadcrumb={[
                    { name: 'Configurações', href: '/settings/stage-colors' },
                    { name: 'Cores dos Estágios' },
                ]}
            >
                {dirty && (
                    <button
                        type="button"
                        onClick={resetAll}
                        className="flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                    >
                        <PiArrowCounterClockwiseBold className="h-4 w-4" />
                        Descartar
                    </button>
                )}
                <Button
                    disabled={!dirty || saveMutation.isPending}
                    onClick={() => saveMutation.mutate()}
                    className="flex items-center gap-2"
                >
                    {saveMutation.isPending ? (
                        <PiSpinnerBold className="h-4 w-4 animate-spin" />
                    ) : (
                        <PiFloppyDiskBold className="h-4 w-4" />
                    )}
                    Salvar cores
                </Button>
            </PageHeader>

            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
                <div className="border-b border-gray-100 px-6 py-4 dark:border-gray-800">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Defina uma cor de destaque para cada estágio do pipeline. A cor será aplicada
                        como fundo suave nas linhas da lista de embarques e no cabeçalho de grupo no painel de tarefas.
                    </p>
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center py-16">
                        <PiSpinnerBold className="h-8 w-8 animate-spin text-gray-400" />
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                        {settings.map((setting, idx) => (
                            <div
                                key={setting.stage}
                                className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:gap-6"
                                style={
                                    setting.row_color
                                        ? { backgroundColor: hexToRgba(setting.row_color, 0.08) }
                                        : undefined
                                }
                            >
                                {/* Número + label */}
                                <div className="flex items-center gap-3 sm:w-56">
                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-500 dark:bg-gray-800">
                                        {idx + 1}
                                    </span>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {setting.label}
                                        </p>
                                        <p className="text-xs text-gray-400">{setting.stage}</p>
                                    </div>
                                </div>

                                {/* Color picker */}
                                <div className="flex-1">
                                    <ColorPicker
                                        value={setting.row_color}
                                        onChange={(color) => handleColorChange(setting.stage, color)}
                                    />
                                </div>

                                {/* Preview */}
                                <div className="flex items-center gap-2">
                                    {setting.row_color ? (
                                        <>
                                            <div
                                                className="h-5 w-5 rounded-full border border-white shadow-sm"
                                                style={{ backgroundColor: setting.row_color }}
                                            />
                                            <span className="font-mono text-xs text-gray-500">{setting.row_color}</span>
                                        </>
                                    ) : (
                                        <span className="text-xs text-gray-400">Sem cor</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
